import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Plus,
  Trash2,
  Search,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit2,
} from 'lucide-react';
import { toast } from 'sonner';
import { ItemSearchModal, ItemSearchResult } from './ItemSearchModal';
import { 
  calculateRowTotals, 
  formatRateForInput, 
  validateAndFormatRate,
  isValidRateInput
} from '@/app/lib/invoiceCalculations';
import { InvoiceLineItem } from '@/app/lib/invoiceCalculations';
import { AmountEntryModal } from './AmountEntryModal';
import { calculateRateFromTotalAmount } from '@/app/lib/purchaseInvoiceCalculations';

// Define the structure for grid columns
export interface GridColumn {
  field: string; // Key in the data object
  header: string; // Display name in the table header
  width?: string; // Optional width (e.g., '150px')
  type?: 'text' | 'number' | 'price' | 'product' | 'actions'; // Cell type for rendering/editing
  editable?: boolean; // Whether the cell is editable (defaults to true if not 'actions' or 'product')
  render?: (row: Record<string, unknown>) => React.ReactNode; // Custom render function (optional)
  sortable?: boolean; // Whether this column can be sorted
}

// Alias ItemSearchResult as ProductSearchResult for consistency within this component context
export type ProductSearchResult = ItemSearchResult;

// Props for the EditableDataGrid component
interface EditableDataGridProps {
  columns: GridColumn[]; // Column definitions
  data: Record<string, unknown>[]; // Array of data objects for rows
  setData: React.Dispatch<React.SetStateAction<Record<string, unknown>[]>>; // Function to update the data state
  productSearchFn?: (searchTerm: string) => Promise<ProductSearchResult[]>; // Async function to search for products
  isLoadingProducts?: boolean; // Loading state for product search
  disabled?: boolean; // Disables editing and actions
  onProductSelect?: (product: ProductSearchResult, rowIndex: number) => Promise<void> | void; // Callback when a product is selected (can be async)
  onChange?: (data: Record<string, unknown>[]) => void; // Optional onChange callback
  siteId?: number; // Added siteId property for tax calculations
  onQuantityChangeForRow?: (rowIndex: number, newQuantity: number) => Promise<void>; // NEW: Callback when quantity changes for a row with applied rule
}

// Define an interface for row data to help with type checking
interface GridRowData extends Record<string, unknown> {
  item_id?: number | string;
  rate?: number | string;
  quantity?: number | string;
  discount_percent?: number | string;
  discount_amount?: number | string;
  amount?: number | string;
  tax_rate_id?: number | string | null;
  applied_sale_rule_id?: number | null;
  item_name?: string;
  item_code?: string;
  description?: string;
  hsn_code?: string;
  tax_percent?: string; // Display string for tax percentage
  taxable_amount?: number;
  tax_amount?: number;
  is_tax_inclusive?: boolean;
  tax_rate_percent?: number; // Store the total tax rate percentage for synchronous calculations
  discounted_rate?: number | string; // Calculated field showing effective rate after discounts
}

export const PurchaseInvoiceDataGrid: React.FC<EditableDataGridProps> = ({
  columns,
  data,
  setData,
  productSearchFn,
  isLoadingProducts = false,
  disabled = false,
  onProductSelect,
  onChange,
  siteId,
  onQuantityChangeForRow,
}) => {
  // State for tracking the currently focused/active cell
  const [activeCell, setActiveCell] = useState<{ row: number; col: string; field: string } | null>(null);
  // State for controlling the item search modal
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  // State to store the index of the row targeted by the modal
  const [modalTargetRowIndex, setModalTargetRowIndex] = useState<number | null>(null);
  // Ref to store the element that triggered the item search modal (for focus return)
  const modalTriggerRef = useRef<HTMLElement | null>(null);
  // State to store the initial search query for the modal
  const [modalInitialQuery, setModalInitialQuery] = useState<string>('');
  // Ref to store references to all input/interactive elements in the grid cells
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | HTMLDivElement | null)[][]>([]);
  // Ref to accumulate typed characters in product cells
  const typedCharsRef = useRef<string>('');
  // Ref to store the timeout for debouncing
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to store the row index for the typing
  const typingRowRef = useRef<number | null>(null);
  // State to show typed characters temporarily
  const [showTyping, setShowTyping] = useState<{ row: number; text: string } | null>(null);
  // State to track which rows are loading pricing
  const [loadingPricing, setLoadingPricing] = useState<Set<number>>(new Set());
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  // State for amount entry modal
  const [amountModalState, setAmountModalState] = useState<{
    isOpen: boolean;
    rowIndex: number | null;
  }>({ isOpen: false, rowIndex: null });

  // Helper function to recalculate row values
  const recalculateRowValues = useCallback((row: GridRowData): GridRowData => {
    console.log('[recalculateRowValues] Input row:', {
      item_id: row.item_id,
      tax_rate_id: row.tax_rate_id,
      quantity: row.quantity,
      rate: row.rate,
      rate_type: typeof row.rate,
      discount_percent: row.discount_percent,
      discount_amount: row.discount_amount,
      is_tax_inclusive: row.is_tax_inclusive
    });
    
    // Convert GridRowData to InvoiceLineItem for calculation
    const lineItem: InvoiceLineItem = {
      item_id: row.item_id,
      quantity: row.quantity,
      rate: row.rate,
      rate_decimal_places: 2, // Use standard 2 decimal places for purchase invoices
      discount_percent: row.discount_percent,
      discount_amount: row.discount_amount,
      tax_rate_id: row.tax_rate_id,
      tax_rate_percent: row.tax_rate_percent,
      is_tax_inclusive: row.is_tax_inclusive,
      taxable_amount: row.taxable_amount,
      tax_amount: row.tax_amount,
      amount: row.amount
    };
    
    // Use centralized calculation
    const totals = calculateRowTotals(lineItem);
    
    console.log('[recalculateRowValues] Calculation results:', totals);
    
    // Format tax percentage for display
    const taxPercent = typeof row.tax_rate_percent === 'number' ? `${row.tax_rate_percent}%` : '-';
    
    // Calculate discounted rate (total amount / quantity)
    const quantity = parseFloat(String(row.quantity || 0));
    let discountedRate = 0;
    if (quantity > 0) {
      discountedRate = totals.amount / quantity;
    }
    
    return {
      ...row,
      taxable_amount: totals.taxable_amount,
      tax_amount: totals.tax_amount,
      amount: totals.amount,
      tax_percent: taxPercent,
      discounted_rate: discountedRate,
    };
  }, []);


  // Function to programmatically focus a specific cell
  const focusCell = useCallback((rowIndex: number, field: string): void => {
    console.log(`[PurchaseInvoiceDataGrid] Requesting focus for cell: row ${rowIndex}, field ${field}`);
    // Use requestAnimationFrame to ensure focus happens after DOM updates
    requestAnimationFrame(() => {
      try {
        // Validate row index
        if (rowIndex < 0 || rowIndex >= inputRefs.current.length) {
          console.warn(`[PurchaseInvoiceDataGrid] Invalid row index for focus: ${rowIndex}`);
          return;
        }

        // Find the column index for the given field
        const colIndex = columns.findIndex(c => c.field === field);
        if (colIndex < 0) {
          console.warn(`[PurchaseInvoiceDataGrid] Field not found for focus: ${field}`);
          return;
        }

        // Ensure the refs array for the row exists
        if (!inputRefs.current[rowIndex]) {
          inputRefs.current[rowIndex] = [];
        }

        // Get the DOM element reference
        const inputElement = inputRefs.current[rowIndex]?.[colIndex];

        if (inputElement) {
          console.log(`[PurchaseInvoiceDataGrid] Focusing element:`, inputElement);
          // Set the active cell state *before* focusing
          setActiveCell({ row: rowIndex, col: colIndex.toString(), field });
          // Focus the element
          inputElement.focus();
          // Select text content if it's an input or textarea
          if (inputElement instanceof HTMLInputElement || inputElement instanceof HTMLTextAreaElement) {
            inputElement.select();
          }
        } else {
          // Log if the element reference wasn't found (might happen during rapid updates)
          console.log(`[PurchaseInvoiceDataGrid] Input element ref not found for row ${rowIndex}, column ${colIndex} (field: ${field})`);
        }
      } catch (error) {
        console.error('[PurchaseInvoiceDataGrid] Error focusing cell:', error);
      }
    });
  }, [columns]); // Dependency: columns array

  // Function to determine the coordinates (row, field) of the next/previous editable cell
  const getNextCellCoordinates = useCallback((
    currentRow: number,
    currentField: string,
    direction: 'next' | 'prev'
  ): { row: number; field: string } | null => {
    // Get a list of fields corresponding to editable columns
    const editableColumns = columns
      .filter(col => col.type !== 'actions' && col.editable !== false)
      .map(col => col.field);

    if (editableColumns.length === 0) return null; // No editable columns

    const currentColIndex = editableColumns.indexOf(currentField);

    // Handle case where the current field itself isn't directly editable (e.g., product cell div)
    if (currentColIndex === -1) {
        const currentActualIndex = columns.findIndex(c => c.field === currentField);
        if (direction === 'next') {
            // Find the next editable column in the same row
            for (let i = currentActualIndex + 1; i < columns.length; i++) {
                if (editableColumns.includes(columns[i].field)) {
                    return { row: currentRow, field: columns[i].field };
                }
            }
            // If no more editable in this row, wrap to the first editable cell of the next row
            if (currentRow < data.length - 1) return { row: currentRow + 1, field: editableColumns[0] };
        } else { // direction 'prev'
             // Find the previous editable column in the same row
            for (let i = currentActualIndex - 1; i >= 0; i--) {
                if (editableColumns.includes(columns[i].field)) {
                    return { row: currentRow, field: columns[i].field };
                }
            }
             // If no more editable before this in the row, wrap to the last editable cell of the previous row
            if (currentRow > 0) return { row: currentRow - 1, field: editableColumns[editableColumns.length - 1] };
        }
        return null; // No editable field found
    }

    // Standard logic for navigating between editable fields
    if (direction === 'next') {
      // Move to the next editable column in the same row
      if (currentColIndex < editableColumns.length - 1) {
        return { row: currentRow, field: editableColumns[currentColIndex + 1] };
      }
      // Move to the first editable column of the next row
      if (currentRow < data.length - 1) {
        return { row: currentRow + 1, field: editableColumns[0] };
      }
      return null; // Reached the end of the grid
    } else { // direction 'prev'
      // Move to the previous editable column in the same row
      if (currentColIndex > 0) {
        return { row: currentRow, field: editableColumns[currentColIndex - 1] };
      }
      // Move to the last editable column of the previous row
      if (currentRow > 0) {
        return { row: currentRow - 1, field: editableColumns[editableColumns.length - 1] };
      }
    }
    return null; // Reached the beginning of the grid
  }, [columns, data.length]); // Dependencies: columns and data length

  // Function to add a new empty row to the grid
  const handleAddRow = useCallback((): void => {
    console.log('[PurchaseInvoiceDataGrid] handleAddRow started.');
    // Create a new row object with default values based on column types
    const newRow: Record<string, unknown> = {};
    columns.forEach(column => {
      if (column.type === 'number' || column.type === 'price') {
        newRow[column.field] = 0;
      } else if (column.field === 'item_id') {
         newRow[column.field] = undefined; // Ensure product cell shows placeholder initially
      } else {
         newRow[column.field] = '';
      }
      // Set specific defaults for common fields
      if (column.field === 'quantity') newRow[column.field] = 1;
      if (column.field === 'discount_percent') newRow[column.field] = 0;
      if (column.field === 'discount_amount') newRow[column.field] = 0;
      if (column.field === 'godown_id') newRow[column.field] = undefined; // Will be set by parent component
      if (column.field === 'amount') newRow[column.field] = 0;
      if (column.field === 'discounted_rate') newRow[column.field] = 0;
    });

    // Update the data state
    const newData = [...data, newRow];
    setData(newData);

    // Focus the first editable cell of the new row after render
    requestAnimationFrame(() => {
        const newRowIndex = newData.length - 1;
        // Prioritize focusing the product cell if it exists
        const productField = columns.find(c => c.type === 'product')?.field;
        // Otherwise, find the first editable field
        const firstEditableField = productField || columns.find(
            c => c.type !== 'actions' && c.editable !== false
        )?.field;

        if (firstEditableField) {
          focusCell(newRowIndex, firstEditableField);
        }
    });
  }, [columns, data, setData, focusCell]); // Dependencies

  // Function to delete a row from the grid
  const handleDeleteRow = useCallback((rowIndex: number): void => {
    // Prevent deleting the last row
    if (data.length === 1) {
      toast.warning("Cannot delete the last row. At least one item is required.");
      return;
    }

    // Filter out the row to be deleted
    const newData = data.filter((_, index) => index !== rowIndex);
    setData(newData);

    // If the deleted row was the last one, focus the first editable cell of the now-last row
    if (rowIndex === data.length - 1 && rowIndex > 0) {
      const editableColumns = columns
        .filter(col => col.type !== 'actions' && col.editable !== false)
        .map(col => col.field);
      if (editableColumns.length > 0) {
        const prevRowIndex = rowIndex - 1;
        const fieldToFocus = editableColumns[0]; // Focus the first editable field
        // Use a short timeout as focus might need the element to be fully settled after state update
        setTimeout(() => focusCell(prevRowIndex, fieldToFocus), 50);
      }
    }
    // Clear the active cell state as the focused row might be gone
    setActiveCell(null);
  }, [data, setData, columns, focusCell]); // Dependencies

  // Function to handle item selection from the search modal
  const handleSelectItem = useCallback(async (item: ProductSearchResult): Promise<void> => {
    if (modalTargetRowIndex === null) return; // Exit if no target row is set
    const rowIndex = modalTargetRowIndex;
    console.log(`[PurchaseInvoiceDataGrid] Item selected for row ${rowIndex}:`, item);

    // --- Stage 1: Update Grid State & Close Modal ---
    // Update the grid's data state with basic item info (name, code, etc.)
    setData(currentData => {
      const newData = [...currentData];
      if (newData[rowIndex]) {
        // Create the updated row object with proper typing
        const updatedRow: GridRowData = {
          ...newData[rowIndex], // Keep existing values like godown_id if present
          item_id: item.id,
          description: String(item.description || item.name || ''), // Use description or name
          item_name: item.name,
          item_code: (item.code && typeof item.code === 'string') ? item.code : '',
          hsn_code: (item.hsn_code && typeof item.hsn_code === 'string') ? item.hsn_code : '', // Add HSN code
          rate: (typeof newData[rowIndex].rate === 'number' || typeof newData[rowIndex].rate === 'string') ? newData[rowIndex].rate : 0, // Keep existing rate if available
          // Keep existing quantity/discount or use defaults if they weren't set
          quantity: (typeof newData[rowIndex].quantity === 'number' || typeof newData[rowIndex].quantity === 'string') && newData[rowIndex].quantity !== undefined
            ? newData[rowIndex].quantity as (string | number)
            : 1,
          discount_percent: (typeof newData[rowIndex].discount_percent === 'number' || typeof newData[rowIndex].discount_percent === 'string') && newData[rowIndex].discount_percent !== undefined
            ? newData[rowIndex].discount_percent as (string | number)
            : 0,
          tax_rate_id: item.tax_rate_id || null, // Get tax_rate_id from the item if available
          tax_percent: '', // Will be updated when tax rate is calculated
          // Reset calculated fields; they depend on the rate
          discount_amount: 0,
          taxable_amount: 0,
          tax_amount: 0,
          amount: 0,
          discounted_rate: 0
        };
        
        newData[rowIndex] = updatedRow;
      }
      return newData; // Return the updated data array
    });

    // Close the modal *after* the initial state update is triggered
    setIsItemModalOpen(false);
    setModalTargetRowIndex(null); // Reset target row
    modalTriggerRef.current = null; // Clear the trigger ref, as selection handles focus now
    setModalInitialQuery(''); // Clear the initial query

    // --- Stage 2: External Action (e.g., Fetch Price) ---
    // Call the optional onProductSelect prop provided by the parent component
    try {
       if (onProductSelect) {
            console.log(`[PurchaseInvoiceDataGrid] Calling onProductSelect for row ${rowIndex}`);
            // Use await in case the parent function is async (e.g., fetching price)
            await onProductSelect(item, rowIndex);
            console.log(`[PurchaseInvoiceDataGrid] onProductSelect finished for row ${rowIndex}`);
            // The parent component (SalesInvoiceForm) is expected to update
            // the rate/amounts by calling `setData` if necessary after fetching.
       } else {
           // Fallback: If no onProductSelect provided, use a mock price
           console.log(`[PurchaseInvoiceDataGrid] No onProductSelect, using mock price lookup for row ${rowIndex}`);
           const mockPrice = Math.round(Math.random() * 100 * 100) / 100; // Random price
           setData(currentData => {
                const newData = [...currentData];
                if (newData[rowIndex]) {
                    // Create a new row object with the mock price
                    const rowData = newData[rowIndex] as GridRowData;
                    const updatedRow: GridRowData = {
                        ...rowData,
                        rate: mockPrice,
                        quantity: typeof rowData.quantity === 'number' ? rowData.quantity : 1,
                        discount_percent: typeof rowData.discount_percent === 'number' ? rowData.discount_percent : 0,
                        discount_amount: typeof rowData.discount_amount === 'number' ? rowData.discount_amount : 0,
                    };
                    
                    // Use the new recalculate function
                    const recalculatedRow = recalculateRowValues(updatedRow);
                    
                    newData[rowIndex] = recalculatedRow; // Put the updated row back into the array
                }
                return newData; // Return the modified data array
           });
           console.log(`[PurchaseInvoiceDataGrid] Mock price ${mockPrice} set for row ${rowIndex}`);
       }
    } catch (error) {
        // Handle errors during the external action or price update
        console.error(`[PurchaseInvoiceDataGrid] Error during onProductSelect or price update for row ${rowIndex}:`, error);
        toast.error("Error updating item details after selection.");
    }

    // --- Stage 3: Focus Next Field ---
    // After all updates (state and potential async actions) are likely settled, focus 'quantity'
    console.log(`[PurchaseInvoiceDataGrid] Attempting to focus 'quantity' field for row ${rowIndex} after item selection.`);
    focusCell(rowIndex, 'quantity');

  }, [modalTargetRowIndex, setData, onProductSelect, focusCell, recalculateRowValues]);


 // Callback function to handle the modal's open/close state changes
 const handleModalOpenChange = useCallback((isOpen: boolean) => {
    console.log("[PurchaseInvoiceDataGrid] Modal open change:", isOpen);
    setIsItemModalOpen(isOpen); // Update the modal visibility state

    if (!isOpen) {
      // Modal is closing
      // Check if the trigger ref still exists (meaning selection didn't clear it)
      if (modalTriggerRef.current) {
         console.log("[PurchaseInvoiceDataGrid] Modal closed (likely via Escape/Outside Click), focusing trigger:", modalTriggerRef.current);
         // Focus the element that originally opened the modal
         // Use a timeout to ensure focus occurs after the modal is fully removed from DOM/transitioned out
         setTimeout(() => {
             modalTriggerRef.current?.focus();
             // If the trigger was an input, select its text
             if (modalTriggerRef.current instanceof HTMLInputElement || modalTriggerRef.current instanceof HTMLTextAreaElement) {
                 modalTriggerRef.current.select();
             }
             // We could try to find the row/col index from the ref to set activeCell,
             // but just focusing the trigger element is usually sufficient for Escape.

         }, 50); // 50ms delay seems reasonable
      }
       // Always clear the target index, trigger ref, and initial query when the modal closes
       setModalTargetRowIndex(null);
       modalTriggerRef.current = null;
       setModalInitialQuery(''); // Clear the initial query
    }
 }, []); // No dependencies needed for this callback


  // Function to handle changes in input cell values
  const handleCellChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    rowIndex: number,
    field: string
  ): Promise<void> => {
    
    const value = e.target.value; 
    
    // For rate field, validate input based on decimal places
    if (field === 'rate') {
      // Use standard 2 decimal places for purchase invoices
      const rateDecimalPlaces = 2;
      
      // Validate rate input
      if (!isValidRateInput(value, rateDecimalPlaces)) {
        return; // Don't update if invalid
      }
    }
    
    const newData = [...data]; 
    if (!newData[rowIndex]) return; 

    const column = columns.find(c => c.field === field); 
    let processedValue: string | number = value; 

    // For number/price fields, store raw input value first, only convert to number if valid
    if (column?.type === 'number' || column?.type === 'price') {
      // Store as string for empty, '-', or partial valid inputs like '1.' to allow user to keep typing
      // Also preserve string when it has trailing zeros after decimal (e.g., "1.0", "1.00", "1.005")
      const hasTrailingZerosAfterDecimal = value.includes('.') && /\.\d*0+$/.test(value);
      
      if (value === '' || value === '-' || value === '.' || value.endsWith('.') || hasTrailingZerosAfterDecimal) {
        processedValue = value; 
      } else {
        // Parse only if it's a complete, valid number
        const floatValue = parseFloat(value);
        // If parsing succeeds, store as number, otherwise keep existing value
        if (!isNaN(floatValue)) {
          // Store the parsed value without rounding - rounding is handled during calculation
          processedValue = floatValue;
        } else {
          // For invalid input, keep raw string to allow continued editing
          processedValue = value;
        }
      }
    }

    // Create the updated row object as GridRowData type for better type safety
    let updatedRow: GridRowData = {
      ...newData[rowIndex],
      [field]: processedValue,
    };

    // Handle mutual exclusivity of discount fields
    if (field === 'discount_percent' && processedValue && parseFloat(String(processedValue)) > 0) {
      updatedRow.discount_amount = 0; // Clear discount amount when percentage is entered
    } else if (field === 'discount_amount' && processedValue && parseFloat(String(processedValue)) > 0) {
      updatedRow.discount_percent = 0; // Clear discount percentage when amount is entered
    }

    // Recalculate when quantity or discount fields change, but NOT for rate field during typing
    // For rate field, we'll recalculate on blur to allow proper input
    if (['quantity', 'discount_percent', 'discount_amount'].includes(field)) {
      // Skip recalculation for empty/partial inputs
      if (value !== '' && value !== '-' && value !== '.' && !value.endsWith('.')) {
        console.log(`[PurchaseInvoiceDataGrid] Recalculating for ${field} change, processedValue: ${processedValue}`);
        const beforeCalc = { ...updatedRow };
        updatedRow = recalculateRowValues(updatedRow);
        console.log(`[PurchaseInvoiceDataGrid] Recalculation result:`, {
          before: { rate: beforeCalc.rate, amount: beforeCalc.amount, taxable: beforeCalc.taxable_amount, tax: beforeCalc.tax_amount },
          after: { rate: updatedRow.rate, amount: updatedRow.amount, taxable: updatedRow.taxable_amount, tax: updatedRow.tax_amount }
        });
      }
    }

    // Update data immediately for UI consistency
    newData[rowIndex] = updatedRow;
    setData(newData);
    if (onChange) {
      onChange(newData);
    }

    // If quantity changed and we have a callback for pricing rules, call it immediately
    // This ensures we get the correct rate before showing calculations
    if (field === 'quantity' && typeof onQuantityChangeForRow === 'function') {
      // Don't trigger callback for incomplete decimal inputs like "2."
      if (!value.endsWith('.')) {
        const numericQuantity = typeof processedValue === 'number' ? processedValue : parseFloat(String(processedValue));
        if (!isNaN(numericQuantity) && numericQuantity > 0) {
          console.log(`[PurchaseInvoiceDataGrid] Quantity changed for row ${rowIndex}. Fetching pricing rules immediately.`);
          // Clear any existing timeout
          if ((window as any).quantityChangeTimeout) {
            clearTimeout((window as any).quantityChangeTimeout);
          }
          // Call immediately to get correct pricing
          (window as any).quantityChangeTimeout = setTimeout(async () => {
            console.log(`[PurchaseInvoiceDataGrid] Calling onQuantityChangeForRow for row ${rowIndex}, qty ${numericQuantity}`);
            // Add loading state
            setLoadingPricing(prev => new Set(prev).add(rowIndex));
            try {
              await onQuantityChangeForRow(rowIndex, numericQuantity);
            } finally {
              // Remove loading state
              setLoadingPricing(prev => {
                const newSet = new Set(prev);
                newSet.delete(rowIndex);
                return newSet;
              });
            }
          }, 100); // Small delay just to debounce rapid typing
        }
      }
    }
  };

 // Function to handle the blur event on input cells (finalize number parsing)
  const handleCellBlur = async (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    rowIndex: number,
    field: string
  ): Promise<void> => {
    console.log(`[PurchaseInvoiceDataGrid] handleCellBlur - field: ${field}, rowIndex: ${rowIndex}, value: ${e.target.value}`);
    
    const column = columns.find(c => c.field === field);
    const currentDataRow = data[rowIndex];
    
    if (!currentDataRow) {
      console.warn(`[PurchaseInvoiceDataGrid] No data row found at index ${rowIndex}`);
      return;
    }
    
    // Get the current value from the data row
    const currentValue = currentDataRow[field];
    console.log(`[PurchaseInvoiceDataGrid] Current value for ${field}: ${currentValue} (type: ${typeof currentValue})`);
    
    // Create a new data array for the update
    const newData = [...data];
    let updatedRow = { ...currentDataRow };
    let needsUpdate = false;
    let needsRecalculation = false;
    
    // Finalize parsing only for number/price fields
    if (column?.type === 'number' || column?.type === 'price') {
      // If the user left empty, partial input, or invalid numeric string, convert to 0
      if (currentValue === '' || currentValue === '-' || currentValue === '.' || 
          (typeof currentValue === 'string' && isNaN(parseFloat(currentValue)))) {
        updatedRow[field] = 0;
        needsUpdate = true;
        needsRecalculation = ['quantity', 'rate', 'discount_percent', 'discount_amount'].includes(field);
        console.log(`[PurchaseInvoiceDataGrid] Converted empty/invalid input to 0 for ${field}`);
      } 
      // If it's already a string that can be parsed to a number, convert it
      else if (typeof currentValue === 'string' && !isNaN(parseFloat(currentValue))) {
        // Don't convert partial inputs that end with decimal point
        if (currentValue.endsWith('.')) {
          // Keep as string to allow continued typing
          console.log(`[PurchaseInvoiceDataGrid] Keeping partial input "${currentValue}" as string for ${field}`);
        }
        // Special handling for rate field
        else if (field === 'rate') {
          // Use standard 2 decimal places for purchase invoices
          const rateDecimalPlaces = 2;
          const validated = validateAndFormatRate(currentValue, rateDecimalPlaces);
          
          if (validated.isValid && validated.numericValue !== null) {
            updatedRow[field] = validated.numericValue;
            needsUpdate = true;
            needsRecalculation = true;
            console.log(`[PurchaseInvoiceDataGrid] Formatted rate: "${currentValue}" to ${validated.numericValue} with ${rateDecimalPlaces} decimals`);
          }
        } else {
          const parsedValue = parseFloat(currentValue);
          updatedRow[field] = parsedValue;
          needsUpdate = true;
          needsRecalculation = ['quantity', 'rate', 'discount_percent', 'discount_amount'].includes(field);
          console.log(`[PurchaseInvoiceDataGrid] Converted string "${currentValue}" to number ${updatedRow[field]} for ${field}`);
        }
      }
      // If it's already a number, still check if we need to recalculate
      else if (typeof currentValue === 'number' && ['quantity', 'rate', 'discount_percent', 'discount_amount'].includes(field)) {
        needsRecalculation = true;
        console.log(`[PurchaseInvoiceDataGrid] Field ${field} is already a number, but needs recalculation`);
      }
    }
    
    // Recalculate if needed
    if (needsRecalculation) {
      console.log(`[PurchaseInvoiceDataGrid] Recalculating on blur for field ${field}`);
      updatedRow = recalculateRowValues(updatedRow as GridRowData);
      needsUpdate = true;
    }
    
    if (needsUpdate) {
      console.log(`[PurchaseInvoiceDataGrid] Updated row on blur:`, updatedRow);
      newData[rowIndex] = updatedRow;
      setData(newData);
      if (onChange) {
        onChange(newData);
      }
    }

    // After parsing and recalculation, if it's a quantity field:
    if (field === 'quantity' && typeof onQuantityChangeForRow === 'function') {
      const finalizedQuantity = parseFloat(String(updatedRow.quantity || 0));
      if (!isNaN(finalizedQuantity) && finalizedQuantity > 0) {
        console.log(`[PurchaseInvoiceDataGrid] Quantity blurred for row ${rowIndex}. Calling onQuantityChangeForRow.`);
        await onQuantityChangeForRow(rowIndex, finalizedQuantity);
      }
    }
  };


  // Function to handle keyboard events within grid cells
  const handleKeyDown = useCallback((
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>,
    rowIndex: number,
    field: string
  ): void => {
    const isProductCell = columns.find(c => c.field === field)?.type === 'product';
    const key = e.key;

    // Example: Prevent default browser action for Ctrl+S if needed
    // if (e.ctrlKey && key === 's') e.preventDefault();

    // NEW: Handle typing in product cells to open modal with search query
    if (isProductCell && key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // User typed a character in product cell
      e.preventDefault();
      
      // Clear any existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // If this is a different row, reset the typed chars
      if (typingRowRef.current !== rowIndex) {
        typedCharsRef.current = key;
        typingRowRef.current = rowIndex;
      } else {
        // Same row, append the character
        typedCharsRef.current += key;
      }
      
      // Store the trigger element
      modalTriggerRef.current = e.currentTarget as HTMLElement;
      
      // Show the typed text temporarily
      setShowTyping({ row: rowIndex, text: typedCharsRef.current });
      
      // Set a new timeout to open the modal after a delay
      typingTimeoutRef.current = setTimeout(() => {
        setModalTargetRowIndex(rowIndex);
        setModalInitialQuery(typedCharsRef.current); // Use accumulated chars
        setIsItemModalOpen(true);
        // Reset typed chars after opening
        typedCharsRef.current = '';
        typingRowRef.current = null;
        setShowTyping(null);
      }, 300); // 300ms delay
      
      return;
    }

    switch (key) {
      case 'Enter':
        e.preventDefault();
        // Clear any pending typing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
          typedCharsRef.current = '';
          typingRowRef.current = null;
          setShowTyping(null);
        }
        if (isProductCell) {
          // Store trigger ref and open modal for product cell with empty query
          modalTriggerRef.current = e.currentTarget as HTMLElement;
          setModalTargetRowIndex(rowIndex);
          setModalInitialQuery(''); // Empty query for Enter key
          setIsItemModalOpen(true);
        } else {
          // Move to the next logical cell (could wrap to next row or add new row)
           const next = getNextCellCoordinates(rowIndex, field, 'next');
           if (next) {
               focusCell(next.row, next.field);
           } else {
               // If Enter pressed on the very last editable cell, add a new row
               handleAddRow();
           }
        }
        break;

      case 'Tab': {
        e.preventDefault();
        // Clear any pending typing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
          typedCharsRef.current = '';
          typingRowRef.current = null;
          setShowTyping(null);
        }
        const direction = e.shiftKey ? 'prev' : 'next';
        const next = getNextCellCoordinates(rowIndex, field, direction);
        if (next) {
          // Move focus to the next/previous cell
          focusCell(next.row, next.field);
        } else if (direction === 'next') {
          // If Tab pressed on last cell, add a new row
          handleAddRow();
        } else {
          // If Shift+Tab pressed on first cell, allow focus to leave the grid
          (e.target as HTMLElement).blur();
        }
        break;
      }

      case 'ArrowUp':
        if (rowIndex > 0) {
          e.preventDefault();
          // Move focus to the same field in the previous row
          focusCell(rowIndex - 1, field);
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (rowIndex < data.length - 1) {
          // Move focus to the same field in the next row
          focusCell(rowIndex + 1, field);
        } else {
          // If ArrowDown pressed on the last row, add a new row
          handleAddRow();
        }
        break;

      case 'Delete':
      case 'Backspace':
         // Use Ctrl/Meta + Delete/Backspace to delete the current row
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleDeleteRow(rowIndex);
        }
        // Allow normal backspace/delete within input fields otherwise
        break;

      case 'F2':
        // Open product search modal if F2 pressed on a product cell
        if (isProductCell) {
          e.preventDefault();
          modalTriggerRef.current = e.currentTarget as HTMLElement; // Store trigger
          setModalTargetRowIndex(rowIndex);
          setModalInitialQuery(''); // Empty query for F2
          setIsItemModalOpen(true);
        }
        break;

      case 'Escape':
        // Clear any pending typing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
          typedCharsRef.current = '';
          typingRowRef.current = null;
          setShowTyping(null);
        }
        // Blur the current cell and clear active state on Escape
        (e.target as HTMLElement).blur();
        setActiveCell(null);
        break;

      default:
        // Allow default behavior for other keys
        break;
    }
  }, [columns, data.length, focusCell, handleAddRow, handleDeleteRow, getNextCellCoordinates]); // Dependencies

  // Sort handler
  const handleSort = useCallback((field: string) => {
    setSortConfig(current => {
      if (current?.field === field) {
        // Toggle direction
        if (current.direction === 'asc') {
          return { field, direction: 'desc' };
        } else if (current.direction === 'desc') {
          return null; // Remove sort
        }
      }
      // New sort field
      return { field, direction: 'asc' };
    });
  }, []);

  // Apply sorting to data
  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;
    
    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];
      
      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      
      // Convert to comparable values
      let aComparable = aValue;
      let bComparable = bValue;
      
      // For numeric fields, ensure we compare numbers
      const column = columns.find(c => c.field === sortConfig.field);
      if (column?.type === 'number' || column?.type === 'price') {
        aComparable = parseFloat(String(aValue)) || 0;
        bComparable = parseFloat(String(bValue)) || 0;
      } else if (sortConfig.field === 'item_name' || sortConfig.field === 'item_id') {
        // For product fields, use item_name for sorting
        aComparable = String(a.item_name || '').toLowerCase();
        bComparable = String(b.item_name || '').toLowerCase();
      } else {
        // For text fields, convert to lowercase string
        aComparable = String(aValue).toLowerCase();
        bComparable = String(bValue).toLowerCase();
      }
      
      // Compare
      if (aComparable < bComparable) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aComparable > bComparable) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sorted;
  }, [data, sortConfig, columns]);

  // Handler for opening amount entry modal
  const handleOpenAmountModal = useCallback((rowIndex: number) => {
    setAmountModalState({ isOpen: true, rowIndex });
  }, []);

  // Handler for confirming amount entry
  const handleAmountEntryConfirm = useCallback((totalAmount: number) => {
    if (amountModalState.rowIndex === null) return;

    const rowIndex = amountModalState.rowIndex;
    const currentRow = data[rowIndex] as GridRowData;

    console.log('[handleAmountEntryConfirm] Current row data:', {
      item_name: currentRow.item_name,
      is_tax_inclusive: currentRow.is_tax_inclusive,
      tax_rate_percent: currentRow.tax_rate_percent,
      quantity: currentRow.quantity,
      totalAmount: totalAmount
    });

    // Calculate the new rate from the total amount
    const result = calculateRateFromTotalAmount(
      totalAmount,
      currentRow.quantity || 1,
      currentRow.tax_rate_percent || 0,
      parseFloat(String(currentRow.discount_percent || 0)),
      parseFloat(String(currentRow.discount_amount || 0)),
      currentRow.is_tax_inclusive || false,
      2 // Use standard 2 decimal places for purchase invoices
    );

    if (result.isValid) {
      // Update the row with the calculated rate
      const newData = [...data];
      newData[rowIndex] = {
        ...currentRow,
        rate: result.rate,
        taxable_amount: result.taxableAmount,
        tax_amount: result.taxAmount,
        amount: totalAmount,
      };

      setData(newData);
      if (onChange) {
        onChange(newData);
      }

      toast.success('Rate calculated successfully');
    } else {
      toast.error(result.error || 'Failed to calculate rate');
    }

    // Close the modal
    setAmountModalState({ isOpen: false, rowIndex: null });
  }, [amountModalState.rowIndex, data, setData, onChange]);

  // --- Render Logic ---
  return (
    // Container with horizontal scroll if needed
    <div className="w-full overflow-x-auto border rounded-md bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            {/* Static numbering column */}
            <TableHead
              style={{ width: '50px', minWidth: '50px' }}
              className="px-2 py-2 font-medium text-sm whitespace-nowrap bg-muted/50 text-center"
            >
              No.
            </TableHead>
            {/* Render table headers */}
            {columns.map((column) => {
              const isSortable = column.sortable !== false && 
                               column.type !== 'actions' && 
                               (column.type === 'product' || column.type === 'number' || column.type === 'price' || column.type === 'text');
              const isSorted = sortConfig?.field === column.field;
              
              return (
                <TableHead
                  key={column.field}
                  // Apply width and minimum width for better layout control
                  style={{ width: column.width, minWidth: column.width }}
                  // Adjust padding, prevent wrapping
                  className={`px-2 py-2 font-medium text-sm whitespace-nowrap bg-muted/50 ${
                    isSortable ? 'cursor-pointer hover:bg-muted/70' : ''
                  }`}
                  onClick={isSortable ? () => handleSort(column.field) : undefined}
                >
                  <div className="flex items-center justify-between">
                    <span>{column.header}</span>
                    {isSortable && (
                      <span className="ml-1">
                        {!isSorted && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        {isSorted && sortConfig.direction === 'asc' && <ArrowUp className="h-3 w-3" />}
                        {isSorted && sortConfig.direction === 'desc' && <ArrowDown className="h-3 w-3" />}
                      </span>
                    )}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Render table rows */}
          {sortedData.map((row, rowIndex) => {
            // Find the original index for this row in the unsorted data
            const originalIndex = data.indexOf(row);
            
            return (
              <TableRow
                  key={originalIndex}
                  // Highlight the row containing the active cell
                  className={activeCell?.row === originalIndex ? 'bg-muted/50' : ''}
                  >
                {/* Static row number */}
                <TableCell className="px-2 py-1 text-center text-sm text-muted-foreground">
                  {rowIndex + 1}
                </TableCell>
                {/* Render cells for the current row */}
                {columns.map((column, colIndex) => {
                  const cellKey = `${originalIndex}-${column.field}`; // Unique key for the cell
                  // Check if this cell is the currently active one
                  const isActive = activeCell?.row === originalIndex && activeCell?.field === column.field;
                  // Function to assign the ref to the correct element in the inputRefs array
                  const elementRef = (el: HTMLInputElement | HTMLTextAreaElement | HTMLDivElement | null): void => {
                    if (!inputRefs.current[originalIndex]) {
                      inputRefs.current[originalIndex] = []; // Ensure row array exists
                    }
                    inputRefs.current[originalIndex][colIndex] = el; // Store the element ref
                  };

                // --- Cell Type Rendering ---

                // 1. Actions Cell (Delete Button)
                if (column.type === 'actions') {
                  return (
                    <TableCell key={cellKey} className="px-1 py-1 text-center"> {/* Minimal padding */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive" // Smaller icon button
                        onClick={() => handleDeleteRow(originalIndex)}
                        // Disable delete for the last row or if grid is disabled
                        disabled={disabled || data.length <= 1}
                        type="button" // Prevent form submission
                        tabIndex={-1} // Remove from default tab order
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  );
                }

                // 2. Product Cell (Clickable Div for Modal)
                if (column.type === 'product') {
                  return (
                    <TableCell key={cellKey} className="px-2 py-1 align-middle"> {/* Standard padding */}
                      <div
                        ref={elementRef as React.Ref<HTMLDivElement>} // Assign ref
                        // Styling: make it look interactive, highlight when active
                        className={`flex items-center h-8 px-3 border rounded-md cursor-pointer transition-colors hover:bg-muted/80 ${isActive ? 'ring-2 ring-ring ring-offset-background ring-offset-2' : 'border-input'} bg-background`}
                        // Open modal on click
                        onClick={(e) => {
                           if (disabled) return;
                           modalTriggerRef.current = e.currentTarget; // Store trigger ref
                           setModalTargetRowIndex(originalIndex);
                           setModalInitialQuery(''); // Clear initial query for click
                           setIsItemModalOpen(true);
                        }}
                        // Handle keyboard events (Enter, F2, navigation)
                        onKeyDown={(e) => !disabled && handleKeyDown(e, originalIndex, column.field)}
                        tabIndex={disabled ? -1 : 0} // Make focusable unless disabled
                        role="button"
                        aria-label="Select product"
                        // Set active cell state on focus
                        onFocus={() => !disabled && setActiveCell({ row: originalIndex, col: colIndex.toString(), field: column.field })}
                        // Clear typing timeout on blur
                        onBlur={() => {
                          if (typingTimeoutRef.current) {
                            clearTimeout(typingTimeoutRef.current);
                            typingTimeoutRef.current = null;
                            typedCharsRef.current = '';
                            typingRowRef.current = null;
                            setShowTyping(null);
                          }
                        }}
                      >
                        {/* Display selected item name or placeholder */}
                        <span className="flex-grow truncate text-sm">
                           {showTyping?.row === originalIndex ? (
                              <span className="text-primary">{showTyping.text}</span>
                           ) : row.item_name ? String(row.item_name) : (
                              <span className="text-muted-foreground italic">Select...</span>
                           )}
                        </span>
                        {/* Search icon */}
                        <Search className="h-4 w-4 ml-2 opacity-50 flex-shrink-0" />
                      </div>
                    </TableCell>
                  );
                }

                // 3. Read-only Calculated Cells (e.g., Amount, Discount Amount)
                if (column.editable === false) {
                   // Get display value, default to empty string
                   let displayValue = String(row[column.field] ?? '');
                   // Format price types to 2 decimal places
                   if (column.type === 'price') {
                       const numericValue = parseFloat(displayValue);
                       // Default to '0.00' if not a valid number
                       displayValue = isNaN(numericValue) ? '0.00' : numericValue.toFixed(2);
                   } else if (column.type === 'number') {
                      // Add formatting for non-price numbers if needed
                   }
                   
                   // Special handling for amount column - add edit button
                   if (column.field === 'amount' && row.item_id) {
                     return (
                       <TableCell key={cellKey} className="px-2 py-1 text-sm align-middle">
                         <div className="flex items-center justify-end gap-1">
                           <span>{displayValue}</span>
                           <Button
                             variant="ghost"
                             size="icon"
                             className="h-6 w-6 opacity-50 hover:opacity-100"
                             onClick={() => handleOpenAmountModal(originalIndex)}
                             disabled={disabled}
                             type="button"
                             tabIndex={-1}
                             title="Calculate rate from amount"
                           >
                             <Edit2 className="h-3 w-3" />
                           </Button>
                         </div>
                       </TableCell>
                     );
                   }
                   
                  return (
                    <TableCell key={cellKey} className={`px-2 py-1 text-sm align-middle ${column.type === 'price' || column.type === 'number' ? 'text-right' : ''}`}> {/* Align numeric right */}
                       {displayValue}
                    </TableCell>
                   );
                }

                // 4. Default Editable Input Cell (Text, Number, Price)
                return (
                  <TableCell key={cellKey} className="px-2 py-1 align-middle"> {/* Standard padding */}
                    <div className="relative">
                      <Input
                        ref={elementRef as React.Ref<HTMLInputElement>} // Assign ref
                        // Determine input type based on column definition
                        type={column.type === 'number' || column.type === 'price' ? 'text' : 'text'}
                        // Get value, default to empty string
                        value={String(row[column.field] ?? '')}
                        // Apply styling, highlight active cell, align numeric right
                        className={`h-8 text-sm bg-background ${isActive ? 'ring-2 ring-ring ring-offset-background ring-offset-2' : ''} ${column.type === 'number' || column.type === 'price' ? 'text-right' : ''}`}
                        // Attach event handlers
                        onChange={(e) => handleCellChange(e, originalIndex, column.field)}
                        onFocus={(e) => {
                          setActiveCell({ row: originalIndex, col: colIndex.toString(), field: column.field });
                          // Select all text when focusing on numeric fields
                          if (column.type === 'number' || column.type === 'price') {
                            e.target.select();
                          }
                        }}
                        onBlur={(e) => handleCellBlur(e, originalIndex, column.field)} // Handle blur for final parsing
                        onKeyDown={(e) => handleKeyDown(e, originalIndex, column.field)}
                        disabled={disabled} // Disable input if grid is disabled
                        // Add placeholder for numeric fields
                        placeholder={column.type === 'number' || column.type === 'price' ? '0' : ''}
                      />
                      {/* Show loading spinner for quantity field when fetching pricing */}
                      {column.field === 'quantity' && loadingPricing.has(originalIndex) && (
                        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </TableCell>
                );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Footer section with Add Item button */}
      <div className="p-2 border-t mt-auto">
        <Button
          variant="outline"
          size="sm"
          className="flex items-center"
          onClick={handleAddRow}
          disabled={disabled} // Disable if grid is disabled
          type="button" // Prevent form submission
          tabIndex={-1} // Remove from default tab order
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Item
        </Button>
      </div>

      {/* Render the Item Search Modal conditionally */}
      {productSearchFn && (
        <ItemSearchModal
          isOpen={isItemModalOpen}
          onOpenChange={handleModalOpenChange} // Use the callback that handles focus return
          onSelectItem={handleSelectItem} // Handles item selection logic
          searchItems={productSearchFn} // Pass down the search function
          isLoading={isLoadingProducts} // Pass down loading state
          initialSearchQuery={modalInitialQuery} // Pass the initial search query
        />
      )}
      
      {/* Render the Amount Entry Modal */}
      {amountModalState.rowIndex !== null && (
        <AmountEntryModal
          isOpen={amountModalState.isOpen}
          onClose={() => setAmountModalState({ isOpen: false, rowIndex: null })}
          onConfirm={handleAmountEntryConfirm}
          currentData={{
            quantity: parseFloat(String(data[amountModalState.rowIndex]?.quantity || 1)),
            taxRatePercent: (data[amountModalState.rowIndex] as GridRowData)?.tax_rate_percent || 0,
            discountPercent: parseFloat(String(data[amountModalState.rowIndex]?.discount_percent || 0)),
            discountAmount: parseFloat(String(data[amountModalState.rowIndex]?.discount_amount || 0)),
            isTaxInclusive: (data[amountModalState.rowIndex] as GridRowData)?.is_tax_inclusive || false,
            rateDecimalPlaces: 2, // Use standard 2 decimal places for purchase invoices
            currentAmount: parseFloat(String(data[amountModalState.rowIndex]?.amount || 0)),
          }}
          itemName={(data[amountModalState.rowIndex] as GridRowData)?.item_name as string}
        />
      )}
    </div>
  );
};